<template>
  <v-container class="pa-0">
    <v-card>
      <v-card-title class="primary">
        <span class="headline white--text">{{ title }}</span>
        <v-spacer/>
        <Icon
          icon="mdi-close"
          iconColor="white"
          :small="false"
          large
          :tooltipText="$t('close')"
          @icon-clicked="$emit('form-closed')"
        />
      </v-card-title>
      <v-card-text>
        <FormWrapper
          :buttonText="submitText"
          @submit="submitCertification"
          :disabled="!isValid"
        >
          <v-container fluid grid-list-md v-if="!loading">
            <v-row v-if="!isEditing">
              <v-col cols="12">
                <v-select
                  :items="availableCertifications"
                  v-model="selectedCertificationId"
                  :label="selectorLabel"
                  :no-data-text="$t('noAvailableCertifications')"
                  item-text="name"
                  item-value="certificationId"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <DatePicker
                @date-picked="setEffectiveDate"
                clearable
                elementId="certification-start-date"
                :startDate="certification.effectiveDate"
                @valid-date="(val) => {
                  isValidStartDate = val
                }"
                :dateLabel="$t('effectiveDate')"/>
              </v-col>
              <v-col cols="12">
                <DatePicker
                @date-picked="setExpirationDate"
                clearable
                elementId="certification-end-date"
                :startDate="expirationStartDate"
                :isValid="datesAreValid ? false : $t('effectiveDateBeforeExpirationDate')"
                @valid-date="(val) => {
                  isValidEndDate = val
                }"
                :dateLabel="$t('expirationDate')"/>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="certification.certificateNumber"
                  :label="$t('certificateNumber')"
                  :maxLength="64"
                  :error-messages="certificateNumberErrors"
                />
              </v-col>
              <v-col cols="12" v-if="!fileInputLoading && !hideFileUpload && isEditing && accountId">
                <v-file-input
                v-model="certificationFile"
                underlined
                color="primary"
                :label="$t('uploadFile')"
                :disabled="importedCertificationFile !== null"
                @change="onFileChanged"
                :hint="certificationFileHint ?? ''"
                persistent-hint
                >
                  <template #append-outer>
                    <Icon
                    :disabled="uploading || !certificationFile || importedCertificationFile !== null"
                    icon="mdi-upload"
                    iconColor="primary"
                    large
                    :small="false"
                    :tooltipText="$t('importFile')"
                    @icon-clicked="uploadToBlob"
                    />
                    <Icon
                    v-if="importedCertificationFile !== null"
                    iconColor="primary"
                    :small="false"
                    large
                    icon="mdi-download"
                    :tooltipText="$t('downloadFile')"
                    @icon-clicked="downloadFile()"
                    />
                    <Icon
                    v-if="importedCertificationFile !== null"
                    icon="mdi-file-document-remove-outline"
                    iconColor="primary"
                    large
                    :small="false"
                    :tooltipText="$t('deleteFile')"
                    @icon-clicked="deleteCertificationFileConfirmation = true"
                    />
                  </template>
                </v-file-input>
              </v-col>
            </v-row>
          </v-container>
        </FormWrapper>
      </v-card-text>
    </v-card>
    <v-dialog v-if="this.importedCertificationFile" v-model="deleteCertificationFileConfirmation" width="400px">
      <ConfirmDialog
      :title="$t('deleteFile')"
      :body="$t('deleteCertificationFileBody', {fileName: this.importedCertificationFile.name, certificationName: accountCertificationLabel(propCertification)})"
      :confirmText="$t('deleteFile')"
      @confirm="confirmDeleteFile"
      @cancel="closeDialog">
      </ConfirmDialog>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import moment from 'moment'
import fieldRules from '@/utils/rules.js'
import { CertificationCategory, AccountFileCategory } from '@/utils/Enumerations.js'
import { accountFilesClient } from '@/utils/AccountFiles'
import { accountCertificationLabel, sameAccountCertification } from '@/utils/AccountCertifications.js'
import { saveAs } from 'file-saver'
import { utcToLocalDate, localToUTC } from '@/utils/DateFormatter.js'
export default {
  name: 'AccountCertificationForm',

  props: {
    propCertification: {
      type: Object,
      default: undefined
    },
    accountId: {
      type: Number,
      default: undefined
    },
    existingCertifications: {
      type: Array,
      default: () => []
    },
    hideFileUpload: {
      type: Boolean,
      default: false
    }
  },

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    DatePicker: () => import('@/components/helper/DatePicker.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue')
  },

  data: () => ({
    loading: undefined,
    certification: {
      effectiveDate: undefined,
      expirationDate: undefined,
      certificateNumber: undefined
    },
    selectedCertificationId: undefined,
    availableCertifications: [],
    rules: fieldRules.rules,
    certificationFile: null,
    uploading: false,
    importedCertificationFile: null,
    deleteCertificationFileConfirmation: false,
    allowedFileMimes: undefined,
    fileInputLoading: true,
    certificationFileHint: undefined,
    users: undefined
  }),

  watch: {
    selectedCertificationId (val) {
      this.certification.certificationId = val
      if (!this.accountId && !this.isEditing) {
        this.certification.name = this.availableCertifications.find(c => c.certificationId === val)?.name
      }
    }
  },

  computed: {
    isEditing () {
      return this.propCertification !== undefined
    },

    submitText () {
      return this.isEditing ? this.$t('saveChanges') : this.$t('addCertification')
    },

    title () {
      return this.isEditing
        ? this.$t('editingAccountCertification', { certification: accountCertificationLabel(this.propCertification) })
        : this.$t('addingCertification')
    },

    certificationTypeId () {
      return this.isEditing ? this.propCertification.certificationId : this.selectedCertificationId
    },

    sameTypeCertifications () {
      return this.existingCertifications.filter(c =>
        c.certificationId === this.certificationTypeId && !sameAccountCertification(c, this.propCertification))
    },

    certificateNumberErrors () {
      if (this.certificationTypeId === undefined) return []
      const number = this.normalizeCertificateNumber(this.certification.certificateNumber)
      const duplicate = this.sameTypeCertifications.find(c => this.normalizeCertificateNumber(c.certificateNumber) === number)
      if (!duplicate) return []
      return [number === ''
        ? this.$t('certificateNumberRequiredForDuplicateType', { certification: duplicate.name })
        : this.$t('duplicateCertificateNumber', { certification: duplicate.name, number: duplicate.certificateNumber })]
    },

    datesAreValid () {
      return !this.certification.effectiveDate ||
        !this.certification.expirationDate ||
        moment(this.certification.effectiveDate).isBefore(this.certification.expirationDate)
    },

    fieldsChanged () {
      return (!this.isSameDay(this.certification.effectiveDate, this.propCertification.effectiveDate) ||
        !this.isSameDay(this.certification.expirationDate, this.propCertification.expirationDate) ||
        (this.certification.certificateNumber?.trim() ?? '') !== (this.propCertification.certificateNumber?.trim() ?? ''))
    },

    isValid () {
      return this.datesAreValid &&
      this.certificateNumberErrors.length === 0 &&
      (this.isEditing || this.selectedCertificationId) &&
      (!this.isEditing || this.fieldsChanged)
    },

    selectorLabel () {
      return this.availableCertifications.length > 0
        ? this.$t('certifications')
        : this.$t('noAvailableCertifications')
    },

    certificationFileCategory () {
      return AccountFileCategory.Certification
    },

    expirationStartDate () {
      return this.certification.expirationDate ? this.certification.expirationDate : moment().add(1, 'day').format()
    }
  },

  async created () {
    this.loading = true
    if (this.isEditing) {
      this.certification = {
        effectiveDate: this.propCertification.effectiveDate,
        expirationDate: this.propCertification.expirationDate,
        certificateNumber: this.propCertification.certificateNumber
      }
      if (!this.hideFileUpload) {
        this.users = await this.getAllUsers()
        this.setFileMimes()
        await this.loadFiles()
      }
    } else {
      this.availableCertifications = (await this.fetchCertifications()).filter(c => c.category === CertificationCategory.Account.value)
      this.certification.effectiveDate = moment().startOf('day').format()
      if (this.availableCertifications.length === 1) this.selectedCertificationId = this.availableCertifications[0].certificationId
    }
    this.loading = false
  },

  methods: {
    ...mapActions('certification', ['fetchCertifications']),
    ...mapActions('user', ['getAllUsers']),
    accountCertificationLabel,

    normalizeCertificateNumber (certificateNumber) {
      return certificateNumber?.trim().toUpperCase() ?? ''
    },

    submitCertification () {
      const certificationRequest = {
        accountId: this.accountId ?? this.propCertification?.accountId,
        certificateNumber: this.certification?.certificateNumber?.trim(),
        effectiveDate: this.certification.effectiveDate,
        expirationDate: this.certification.expirationDate
      }

      if (this.isEditing) {
        certificationRequest.certificationId = this.propCertification.certificationId
        certificationRequest.accountCertificationId = this.propCertification.accountCertificationId
        certificationRequest.localId = this.propCertification.localId
        this.$emit('certification-edited', certificationRequest)
      } else {
        certificationRequest.certificationId = this.selectedCertificationId
        this.$emit('certification-added', { ...certificationRequest, name: this.certification.name })
      }
    },

    setEffectiveDate (effectiveDate) {
      this.certification.effectiveDate = effectiveDate ? localToUTC(effectiveDate) : undefined
    },

    setExpirationDate (expirationDate) {
      this.certification.expirationDate = expirationDate ? localToUTC(expirationDate) : undefined
    },

    isSameDay (date1, date2) {
      if ((date1 === null || date1 === undefined) && (date2 === null || date2 === undefined)) return true
      if ((date1 === null || date1 === undefined) && !(date2 === null || date2 === undefined)) return false
      if (!(date1 === null || date1 === undefined) && (date2 === null || date2 === undefined)) return false
      return moment(date1).isSame(date2, 'day')
    },

    async loadFiles () {
      const loadedFiles = await accountFilesClient.getFileByPolicy(this.propCertification, this.certificationFileCategory, this.existingCertifications)
      const loadedFile = loadedFiles && loadedFiles.length > 0 ? loadedFiles[0] : null
      if (loadedFile) {
        this.setFile(loadedFile)
      }
      this.fileInputLoading = false
    },

    confirmDeleteFile () {
      this.deleteFile(this.importedCertificationFile)
      this.importedCertificationFile = null
      this.certificationFile = null
      this.closeDialog()
    },

    closeDialog () {
      this.deleteCertificationFileConfirmation = false
    },

    setFile (file) {
      this.certificationFile = file
      this.importedCertificationFile = file
      this.certificationFileHint = this.$t('uploadedDateByUser', { date: this.dateFromMilliseconds(this.importedCertificationFile.date), user: this.userFromId(this.importedCertificationFile.user) })
    },

    async uploadToBlob () {
      if (this.uploading || !this.certificationFile) return
      if (!this.validateUpload(this.certificationFile)) return
      this.uploading = true
      try {
        await accountFilesClient.uploadAccountFile(this.certificationFile, this.propCertification, this.certificationFileCategory)
        await this.loadFiles()
        this.$emit('file-edited')
      } finally {
        this.uploading = false
      }
    },

    async deleteFile (file) {
      await accountFilesClient.deleteFile(file.ref).then(async () => {
        this.certificationFile = null
        this.certificationFileHint = undefined
        await this.loadFiles()
        this.deleteConfirmation = false
        this.$emit('file-edited')
      })
    },

    async downloadFile () {
      const fetchedFile = await fetch(this.importedCertificationFile.ref)
      const blob = await fetchedFile.blob()
      saveAs(blob, decodeURIComponent(fetchedFile.url.split('?')[0].split('/').pop()))
    },

    onFileChanged (e) {
      this.certificationFile = e
    },

    validateUpload (file) {
      const limit = 1000 * 1000
      if (file.size > limit) { // Exceeds 1MB
        this.setSnackError(this.$t('fileTooLargeSnack', { limit: '1MB' }))
        this.certificationFile = null
        return false
      }
      if (!this.allowedFileMimes.has(file.type)) {
        this.setSnackError(this.$t('fileMimeNotAllowedSnack'))
        this.certificationFile = null
        return false
      }
      return true
    },

    dateFromMilliseconds (milliseconds) {
      const d = new Date(+milliseconds)
      return utcToLocalDate(d, 'MM/DD/YYYY - LT')
    },

    userFromId (id) {
      return this.users.find(u => u.applicationUserId?.toString() === id.toString())?.name
    },

    setFileMimes () {
      this.allowedFileMimes = new Set([
        'application/pdf',
        'application/msword',
        'text/plain',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
        'image/png',
        'image/jpeg',
        'image/heic',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.ms-word'
      ])
    }
  }
}
</script>
